import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bank, ArrowRight, CheckCircle, WarningCircle, X as XIcon } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * QBO Sync bar for the Invoices page. Shows:
 *   - "N ready to sync · N already · N drafts skipped" preview from server
 *   - "Sync N invoices" button (disabled if 0 syncable)
 *   - Results modal after sync completes
 *
 * Gracefully hides itself when QBO isn't configured (via /qbo/status), so
 * a fleet that hasn't connected QuickBooks yet never sees the noise.
 */
export default function InvoicesQboSyncBar({ onSyncComplete } = {}) {
  const [qboStatus, setQboStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [qbo, prev] = await Promise.all([
        client.get("/qbo/status"),
        client.get("/invoices/qbo-sync/preview"),
      ]);
      setQboStatus(qbo.data);
      setPreview(prev.data);
    } catch {/* silent */}
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  // Not configured OR not connected — render nothing (keeps invoices page clean
  // for users who haven't set up QBO yet).
  if (loading) return null;
  if (qboStatus?.configured === false) return null;
  if (qboStatus?.connected !== true) return null;

  const syncableCount = preview?.syncable?.length || 0;
  const alreadyCount = preview?.already_synced_count || 0;
  const draftCount = preview?.draft_count || 0;

  const doSync = async () => {
    if (syncableCount === 0) return;
    if (!window.confirm(`Push ${syncableCount} invoice${syncableCount === 1 ? "" : "s"} to QuickBooks now?`)) return;
    setSyncing(true);
    try {
      const { data } = await client.post("/invoices/qbo-sync", null, { timeout: 120000 });
      setResult(data);
      if (data.synced > 0) {
        toast.success(`Synced ${data.synced} invoice${data.synced === 1 ? "" : "s"} to QuickBooks`);
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} sync${data.failed === 1 ? "" : "s"} failed — see details`);
      }
      await refresh();
      onSyncComplete?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  return (
    <>
      <div
        className="card-surface p-3 mb-4 flex items-center gap-3 flex-wrap"
        data-testid="invoices-qbo-sync-bar"
      >
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
            <Bank size={16} weight="fill" />
          </div>
          <div>
            <div className="text-xs font-semibold text-white leading-tight">QuickBooks sync</div>
            <div className="text-[10px] text-neutral-500 leading-tight">{qboStatus.company_name || "Connected"}</div>
          </div>
        </div>

        <div className="flex-1 flex items-center gap-3 flex-wrap text-xs">
          <span className="text-neutral-300">
            <b className="text-white" data-testid="invoices-qbo-sync-ready-count">{syncableCount}</b> ready
          </span>
          <span className="text-neutral-600">·</span>
          <span className="text-neutral-500">{alreadyCount} already synced</span>
          <span className="text-neutral-600">·</span>
          <span className="text-neutral-500">{draftCount} draft{draftCount === 1 ? "" : "s"} skipped</span>
        </div>

        <button
          onClick={doSync}
          disabled={syncing || syncableCount === 0}
          data-testid="invoices-qbo-sync-btn"
          className={`text-xs rounded-full px-4 py-2 inline-flex items-center gap-2 transition-colors ${
            syncableCount === 0
              ? "border border-white/10 text-neutral-600 cursor-not-allowed"
              : "bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
          } disabled:opacity-60`}
        >
          {syncing ? (
            <>
              <div className="w-3 h-3 rounded-full border-2 border-black/30 border-t-black animate-spin" />
              Syncing {syncableCount}...
            </>
          ) : syncableCount === 0 ? (
            "Nothing to sync"
          ) : (
            <>
              Sync {syncableCount} invoice{syncableCount === 1 ? "" : "s"} <ArrowRight size={11} weight="bold" />
            </>
          )}
        </button>
      </div>

      {result && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
          data-testid="invoices-qbo-sync-result-modal"
        >
          <div className="min-h-full flex items-start justify-center py-8">
            <div className="w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
                    <Bank size={12} weight="fill" /> QuickBooks batch sync
                  </div>
                  <div className="text-lg font-semibold text-white">Sync complete</div>
                </div>
                <button
                  onClick={() => setResult(null)}
                  className="text-neutral-500 hover:text-white"
                  data-testid="invoices-qbo-sync-result-close"
                >
                  <XIcon size={18} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="card-surface p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Synced</div>
                    <div className="text-2xl font-display font-semibold text-emerald-400 mt-1"
                         data-testid="invoices-qbo-sync-result-synced">{result.synced}</div>
                  </div>
                  <div className="card-surface p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Failed</div>
                    <div className={`text-2xl font-display font-semibold mt-1 ${result.failed ? "text-rose-300" : "text-neutral-600"}`}
                         data-testid="invoices-qbo-sync-result-failed">{result.failed}</div>
                  </div>
                  <div className="card-surface p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Skipped</div>
                    <div className="text-2xl font-display font-semibold text-neutral-500 mt-1">{result.skipped}</div>
                  </div>
                </div>
                {result.results?.length > 0 && (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {result.results.map((r, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-2 text-xs p-2 rounded border ${
                          r.status === "synced"
                            ? "border-white/5 bg-white/[0.02] text-neutral-300"
                            : "border-rose-500/20 bg-rose-500/5 text-rose-200"
                        }`}
                      >
                        {r.status === "synced"
                          ? <CheckCircle size={12} weight="fill" className="text-emerald-400 flex-shrink-0" />
                          : <WarningCircle size={12} weight="fill" className="text-rose-400 flex-shrink-0" />}
                        <span className="font-mono text-[10px] text-neutral-500 w-16 truncate">
                          {r.invoice_number || r.invoice_id?.slice(0, 8) || `#${i + 1}`}
                        </span>
                        <span className="flex-1 truncate">
                          {r.status === "synced" ? `QBO #${r.qbo_id}` : r.error}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
